import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import { endOfToday, format, isAfter, isBefore, isValid, parseISO, startOfToday } from 'date-fns';
import { Box, Button, ClickAwayListener, Stack, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import * as R from 'ramda';

// Redux
import { compose } from 'redux';

// Util
import { formatNumber, strToInt } from 'utils/helpers/format';
import { validateIntGTZero, validateRequiredAutocomplete } from 'utils/helpers/formValidations';
import { DATE_GTE_NOW, INVALID_DATE, REQUIRED } from 'utils/helpers/validationMessages';

// Components
import { DrawerLayoutWrapper, MAlert, MAutocomplete, MDialog } from 'common';
import StatusChip from '../status-chip/StatusChip';

// Styles
// eslint-disable-next-line import/no-cycle
import EditPtoDialog from './EditPtoDialog';

const getValidationMessage = R.cond([
  [R.isNil, R.always(REQUIRED)],
  [R.complement(isValid), R.always(INVALID_DATE)],
  [date => isBefore(date, startOfToday()), R.always(DATE_GTE_NOW)],
  [R.T, R.always('')],
]);

const boldText = { fontWeight: 700 };
const contentWrapper = {
  '&:first-of-type': {
    flexDirection: 'column',
  },
  '&:nth-of-type(n+1)': {
    justifyContent: 'space-between',
  },
  display: 'flex',
  mb: 1.5,
};
const contentWrapperChild = {
  '&:first-of-type': {
    justifyContent: 'flex-end',
  },
  display: 'flex',
  flexDirection: 'column',
};

const EditPtoPanel = props => {
  const {
    editPtoDetails,
    fetchAffiliates,
    fetchEditPtoDetails,
    fetchNotifications,
    fetchOriginatorLocationProducts,
    fetchOriginatorLocationProductReidVaporPressures,
    fetchProductBalance,
    fetchReidVaporPressures,
    onClose,
    originatorLocationProductReidVaporPressures,
    productBalance,
    productOptions,
    ptoNumber,
    recipientOptions,
    refreshNotifications,
    resetCreatePto,
    resetEditPto,
    rvpOptions,
    uniqueLocationProductRVPs,
    updateEditPtoField,
    setCurrentPtoDialogueOpen,
  } = props;

  const {
    command,
    dataCompany,
    dataCompanyId,
    errorMessage,
    inventorySelection,
    location,
    locationId,
    originator,
    originatorIdName,
    pending: editPtoDetailsPending,
    productTransferOrderId,
    reconsignmentControl,
    status,
  } = editPtoDetails;

  const currentPtoDialogueOpen = useSelector(state => state.pto.ptoDrawer.currentPtoDialogueOpen);

  const [pickerDate, setPickerDate] = useState(null);
  const [transferDateValid, setTransferDateValid] = useState(true);
  const [transferDateTouched, setTransferDateTouched] = useState(false);
  const [validation, setValidation] = useState({
    product: '',
    recipient: '',
    volume: '',
  });
  const [editPtoDialogOpen, setEditPtoDialogOpen] = useState(false);

  const { productId, recipientIdName, referenceId, rvp, transferDate, volume } = command;
  const volumeExceedsProductBalance = productBalance != null && R.gt(volume, productBalance);
  const [initialState, setInitialState] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [wasDateAutoCorrected, setWasDateAutoCorrected] = useState(false);

  // Reset states when order changes
  useEffect(() => {
    setInitialState(null);
    setWasDateAutoCorrected(false);
    setPickerDate(null);
    setTransferDateTouched(false);
  }, [ptoNumber]);

  // Capture initial state immediately when data finishes loading from API
  useEffect(() => {
    if (!editPtoDetailsPending && productId != null && initialState === null) {
      setInitialState({ ...command });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPtoDetailsPending, productId]);

  useEffect(() => {
    // test if we can use current transferDate
    if (transferDate && !pickerDate) {
      // remove timezone from transferDate
      const dateOnly = transferDate.split('T')[0];
      // convert the transferDate to a comparable format
      const parsedTransferDate = parseISO(dateOnly);
      // determine if transfer date can be used
      const isTransferDateValid = !isBefore(parsedTransferDate, startOfToday());
      const initialDate = isTransferDateValid ? parsedTransferDate : startOfToday();
      // Track if date was auto-corrected
      if (!isTransferDateValid) {
        setWasDateAutoCorrected(true);
      }

      setPickerDate(initialDate);
      updateEditPtoField('transferDate', format(initialDate, 'yyyy-MM-dd'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerDate, transferDate, updateEditPtoField]);

  useEffect(() => {
    if (dataCompanyId && originatorIdName && locationId && productId) {
      fetchOriginatorLocationProductReidVaporPressures({
        locationId,
        originator: {
          companyCustomerIdName: originatorIdName,
          dataCompanyId,
        },
        productId,
      });
    }
  }, [
    dataCompanyId,
    fetchOriginatorLocationProductReidVaporPressures,
    originatorIdName,
    locationId,
    productId,
  ]);

  useEffect(() => {
    fetchEditPtoDetails({ ProductTransferOrderId: ptoNumber });
  }, [fetchEditPtoDetails, ptoNumber]);

  useEffect(() => {
    if (dataCompanyId) {
      fetchReidVaporPressures({
        dataCompanyId,
      });
    }
  }, [dataCompanyId, fetchReidVaporPressures]);

  useEffect(() => {
    if (dataCompanyId) {
      fetchOriginatorLocationProducts({
        locationId,
        originator: {
          CompanyCustomerIdName: originatorIdName,
          dataCompanyId,
        },
      });
    }
  }, [dataCompanyId, fetchOriginatorLocationProducts, locationId, originatorIdName]);

  useEffect(() => {
    if (dataCompanyId) {
      fetchAffiliates({
        OrderBy: 'frequency',
        dataCompanyId,
      });
    }
  }, [dataCompanyId, fetchAffiliates]);

  useEffect(() => {
    if (originatorIdName) {
      fetchProductBalance({
        inventorySelection,
        locationId,
        originator: {
          CompanyCustomerIdName: originatorIdName,
          dataCompanyId,
        },
        productId,
        reconsignmentControl,
        rvp,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originatorIdName]);

  // cleanup
  useEffect(() => {
    return () => {
      resetEditPto();
      setEditPtoDialogOpen(false);
    };
  }, [resetEditPto]);

  const checkUpdates = () => {
    if (!initialState) {
      return false;
    }
    const normalizeValue = value => (value === '' ? null : value);

    // Normalize date to YYYY-MM-DD format for comparison
    const normalizeDateForComparison = dateStr => {
      if (!dateStr) return null;
      // Extract just the date part (YYYY-MM-DD) from formats like '2026-02-17T0:0:0:00-06:00' or '2026-02-17'
      return dateStr.split('T')[0];
    };

    const compareValues = (state1, state2) => {
      // Compare volumes as integers
      const volumesEqual =
        parseInt(normalizeValue(state1.volume) || 0) ===
        parseInt(normalizeValue(state2.volume) || 0);

      // Compare referenceIds as strings
      const refsEqual =
        String(normalizeValue(state1.referenceId) || '') ===
        String(normalizeValue(state2.referenceId) || '');

      // Normalize dates for comparison
      const date1 = normalizeDateForComparison(state1.transferDate);
      const date2 = normalizeDateForComparison(state2.transferDate);
      const datesEqual = date1 === date2;

      // Compare rest of properties (excluding volume, referenceId, AND transferDate)
      const restEqual = R.equals(
        R.omit(['volume', 'referenceId', 'transferDate'], state1),
        R.omit(['volume', 'referenceId', 'transferDate'], state2)
      );

      return volumesEqual && refsEqual && datesEqual && restEqual;
    };

    const hasChanges = !compareValues(initialState, editPtoDetails.command);
    return hasChanges;
  };
  const isFutureDate = pickerDate && isValid(pickerDate) && isAfter(pickerDate, endOfToday());
  const isEditFormValid = () => {
    return (
      R.isEmpty(validation.recipient) &&
      R.isEmpty(validation.product) &&
      R.isEmpty(validation.volume) &&
      transferDateValid &&
      (isFutureDate || !volumeExceedsProductBalance) &&
      checkUpdates()
    );
  };

  const handleSetTransferDate = newValue => {
    setTransferDateTouched(true);
    setPickerDate(newValue);
    if (isValid(newValue) && !isBefore(newValue, startOfToday())) {
      updateEditPtoField('transferDate', format(newValue, 'yyyy-MM-dd'));
      setTransferDateValid(true);
    } else {
      setTransferDateValid(false);
    }
  };

  const updateRecipient = val => updateEditPtoField('recipientIdName', val);
  const updateProduct = val => updateEditPtoField('productId', val);
  const updateRVP = val => updateEditPtoField('rvp', val);
  const updateVolume = val => updateEditPtoField('volume', val, true);
  const updateReference = val => updateEditPtoField('referenceId', val);

  const handleSetRecipient = R.compose(
    setValidation,
    R.assoc('recipient'),
    validateRequiredAutocomplete,
    R.tap(updateRecipient),
    R.prop('value')
  );

  const handleSetProduct = R.compose(
    R.tap(updateRVP),
    R.always(null),
    setValidation,
    R.assoc('product'),
    validateRequiredAutocomplete,
    R.tap(updateProduct),
    R.prop('value')
  );

  const handleSetRvp = R.o(R.tap(updateRVP), R.prop('value'));

  const handleUpdateVolume = R.compose(
    setValidation,
    R.assoc('volume'),
    validateIntGTZero,
    R.tap(updateVolume),
    R.tap(strToInt),
    R.path(['target', 'value'])
  );

  const getVolumeHelperText = () => {
    if (!isFutureDate && volumeExceedsProductBalance) {
      return 'Volume cannot be greater than the product balance';
    }
    return validation.volume;
  };

  const handleUpdateReferenceId = R.compose(R.tap(updateReference), R.path(['target', 'value']));

  const togglePtoDialog = (num, type) =>
    setCurrentPtoDialogueOpen({
      ptoDialogueType: type,
      ptoNumber: num,
    });

  const isDisabled = !isEditFormValid() || (!checkUpdates() && !wasDateAutoCorrected);
  const transferDateValidationMessage = transferDateTouched ? getValidationMessage(pickerDate) : '';
  const buttons = (
    <>
      <Button onClick={() => togglePtoDialog(productTransferOrderId, 'cancel')} variant="outlined">
        Cancel Order
      </Button>
      <Button disabled={isDisabled} onClick={() => setEditPtoDialogOpen(true)}>
        Submit Order
      </Button>
    </>
  );

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      onClickAway={() => {
        if (currentPtoDialogueOpen === 'cancel') {
          return;
        }
        if (checkUpdates()) {
          setOpenDialog(true);
        } else {
          onClose();
        }
      }}
      touchEvent="onTouchStart"
    >
      <Box sx={{ height: '100%' }}>
        <DrawerLayoutWrapper
          buttons={buttons}
          headerButtonText="CLOSE"
          isLoading={editPtoDetailsPending}
          onHeaderButtonClick={() => {
            if (checkUpdates()) {
              setOpenDialog(true);
            } else {
              onClose();
            }
          }}
          title="Edit Order"
        >
          <Box
            sx={{
              height: '100%',
              padding: theme => theme.spacing(1.5, 3, '86px'),
              width: '100%',
            }}
          >
            <Box sx={contentWrapper}>
              <Typography className="css-heap-redact" variant="caption">
                Originator
              </Typography>
              <Typography
                className="css-heap-redact"
                component="span"
                sx={boldText}
                variant="body1"
              >
                {originator.replace(' -', '')}
              </Typography>
            </Box>
            <Box sx={contentWrapper}>
              <StatusChip status={status} />
              <Typography variant="body2">{dataCompany}</Typography>
            </Box>
            <Box sx={contentWrapper}>
              <Box sx={contentWrapperChild}>
                <Typography variant="caption">Location</Typography>
                <Typography sx={boldText} variant="body2">
                  {location.replace(' -', '')}
                </Typography>
              </Box>
              <Box sx={contentWrapperChild}>
                <Typography sx={{ textAlign: 'end' }} variant="caption">
                  PTO Number
                </Typography>
                <Typography sx={{ ...boldText, textAlign: 'end' }} variant="body2">
                  {productTransferOrderId}
                </Typography>
              </Box>
            </Box>
            {errorMessage && (
              <Box
                sx={{
                  mb: 1.5,
                }}
              >
                <MAlert message={errorMessage} severity="error" />
              </Box>
            )}
            <Stack
              spacing={2}
              sx={{
                borderTop: theme => `0.1em solid ${theme.palette.graphic.light}`,
                pb: 1.5,
                pt: 3,
                px: 0,
              }}
            >
              <DatePicker
                disablePast
                id="transfer-date"
                label="Transfer Date"
                onChange={handleSetTransferDate}
                slotProps={{
                  textField: {
                    error: !!transferDateValidationMessage,
                    helperText: transferDateValidationMessage,
                    placeholder: 'mm/dd/yyyy',
                  },
                }}
                value={pickerDate}
              />
              <MAutocomplete
                classNames="css-heap-redact"
                error={!R.isEmpty(validation.recipient)}
                helperText={validation.recipient}
                id="recipient-select"
                label="Recipient"
                loading={false}
                name="recipient"
                onChange={R.flip(handleSetRecipient)}
                options={recipientOptions}
                value={recipientIdName}
              />
              <MAutocomplete
                classNames="css-heap-redact"
                error={!R.isEmpty(validation.product)}
                helperText={validation.product}
                id="product-select"
                label="Product"
                loading={false}
                name="product"
                onChange={R.flip(handleSetProduct)}
                options={productOptions}
                value={productId}
              />
              {uniqueLocationProductRVPs.length > 1 && (
                <MAutocomplete
                  defaultValue={{
                    label: 'Any',
                    value: null,
                  }}
                  id="rvp-select"
                  label="RVP"
                  loading={false}
                  name="rvp"
                  onChange={R.flip(handleSetRvp)}
                  options={[
                    ...(uniqueLocationProductRVPs ||
                      originatorLocationProductReidVaporPressures ||
                      rvpOptions),
                  ]}
                  value={rvp}
                />
              )}
              {!isFutureDate && (
                <Box
                  sx={{
                    alignItems: 'center',
                    backgroundColor: 'secondary.light',
                    border: '1px solid',
                    borderColor: 'secondary.medium',
                    borderRadius: 0.5,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    marginTop: 2,
                    px: 0,
                    py: 0.5,
                  }}
                >
                  <Typography className="css-heap-redact" variant="caption">
                    Product Balance
                  </Typography>
                  <Typography className="css-heap-redact" sx={{ fontWeight: 700 }} variant="body2">
                    {productBalance && formatNumber(productBalance)} bbls
                  </Typography>
                </Box>
              )}
              <TextField
                className="css-heap-redact"
                error={
                  !R.isEmpty(validation.volume) || (!isFutureDate && volumeExceedsProductBalance)
                }
                fullWidth
                helperText={getVolumeHelperText()}
                id="volume"
                label="Volume (bbls)"
                onChange={handleUpdateVolume}
                value={volume}
              />
              <TextField
                fullWidth
                id="referenceId"
                label="Reference Id"
                onChange={handleUpdateReferenceId}
                value={referenceId ?? ''}
              />
            </Stack>
          </Box>
          <EditPtoDialog
            closeDialog={() => setEditPtoDialogOpen(false)}
            dialogOpen={editPtoDialogOpen}
            fetchNotifications={fetchNotifications}
            ptoNumber={ptoNumber}
            refreshNotifications={refreshNotifications}
            resetCreatePto={resetCreatePto}
          />
          <MDialog
            header="Unsaved Changes"
            leftButtonAction={() => {
              setOpenDialog(false);
              onClose();
            }}
            leftButtonText="Continue"
            onClose={() => false}
            open={openDialog}
            rightButtonAction={() => setOpenDialog(false)}
            rightButtonText="Keep Editing"
          >
            <Typography color="textPrimary" component="span" variant="subtitle1">
              Are you sure you want to leave this page? You have unsaved PTO changes.
            </Typography>
          </MDialog>
        </DrawerLayoutWrapper>
      </Box>
    </ClickAwayListener>
  );
};

EditPtoPanel.propTypes = {
  editPtoDetails: shape({
    command: shape({
      productId: oneOfType([number, string]),
      recipientIdName: string,
      referenceId: string,
      rvp: oneOfType([number, string, arrayOf(number)]),
      volume: oneOfType([number, string]),
    }),
    dataCompany: string,
    dataCompanyId: number,
    errorMessage: string,
    inventorySelection: string,
    location: string,
    locationId: number,
    originator: string,
    originatorIdName: string,
    pending: bool,
    productTransferOrderId: oneOfType([number, string]),
    reconsignmentControl: string,
  }).isRequired,
  fetchAffiliates: func.isRequired,
  fetchEditPtoDetails: func.isRequired,
  fetchNotifications: func.isRequired,
  fetchOriginatorLocationProductReidVaporPressures: func.isRequired,
  fetchOriginatorLocationProducts: func.isRequired,
  fetchProductBalance: func.isRequired,
  fetchReidVaporPressures: func.isRequired,
  onClose: func.isRequired,
  originatorLocationProductReidVaporPressures: arrayOf(number),
  productBalance: number,
  productOptions: arrayOf(
    shape({
      label: string,
      value: number,
    })
  ).isRequired,
  ptoNumber: number.isRequired,
  recipientOptions: arrayOf(
    shape({
      label: string,
      value: string,
    })
  ).isRequired,
  refreshNotifications: bool,
  resetCreatePto: func.isRequired,
  resetEditPto: func.isRequired,
  rvpOptions: arrayOf(
    shape({
      label: string,
      value: oneOfType([number, string]),
    })
  ).isRequired,
  setCurrentPtoDialogueOpen: func.isRequired,
  uniqueLocationProductRVPs: arrayOf(
    shape({
      label: string,
      value: oneOfType([number, string]),
    })
  ),
  updateEditPtoField: func.isRequired,
};

EditPtoPanel.defaultProps = {
  // onViewDetails: () => {},
  originatorLocationProductReidVaporPressures: [],
  productBalance: null,
  refreshNotifications: null,
  uniqueLocationProductRVPs: [],
};

export default compose()(EditPtoPanel);
